import { AxiosError } from "axios";
import {
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from "react-query";

import { ILinkView } from "../../types";
import { api } from "../../utils";
import { IDeleteLinkViewParams } from "../../utils/api";
import { useAuth } from "../useAuth";

export interface IDeleteLinkViewMutationParameters
    extends Omit<IDeleteLinkViewParams, "token"> {}

/**
 * @author Nikhil babar <nikhil.babar@houseofx.in>
 */
export const useDeleteLinkView = (
    invalidQueryKeys?: string | string[],
    options?: UseMutationOptions<
        ILinkView,
        AxiosError,
        IDeleteLinkViewMutationParameters,
        unknown
    >,
): UseMutationResult<
    ILinkView,
    AxiosError,
    IDeleteLinkViewMutationParameters
> => {
    const auth = useAuth();
    const queryClient = useQueryClient();

    return useMutation<
        ILinkView,
        AxiosError,
        IDeleteLinkViewMutationParameters
    >({
        ...options,
        mutationFn: (
            parameters: IDeleteLinkViewMutationParameters,
        ): Promise<ILinkView> => {
            if (!auth.token) {
                throw new Error(
                    "Cannot delete link-view without authorization token.",
                );
            }

            return api.LinkView.delete({
                ...parameters,
                token: auth.token,
            });
        },
        onSuccess: (
            linkView: ILinkView,
            variables: IDeleteLinkViewMutationParameters,
            context: unknown,
        ) => {
            if (invalidQueryKeys !== undefined) {
                queryClient.invalidateQueries(invalidQueryKeys);
            }

            options?.onSuccess?.(linkView, variables, context);
        },
    });
};
